/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FC, Ref } from 'react';
import { useRef, useState } from 'react';
import {
  CustomIconButton,
  Draggable,
  Resizable,
  useMakeSureContainerStaysInsideWindow,
} from '@opengeoweb/shared';
import { Clock as ClockIcon } from '@opengeoweb/theme';
import { ClockContainer } from './ClockContainer';
import { useWebmapReactTranslation } from '../../utils/i18n';

export const MIN_WIDTH = 240;
export const MIN_HEIGHT = 32;
const MIN_FONT_SIZE = 14; // bigger font size will cause render delay in LCP
const FONT_SIZE_WIDTH_RATIO = MIN_FONT_SIZE / MIN_WIDTH;

const resizeHandlePlacement = {
  top: false,
  right: false,
  bottom: false,
  left: false,
  topRight: true,
  bottomRight: true,
  bottomLeft: true,
  topLeft: true,
};

export interface Position {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export const Clock: FC<{
  optionsElement: Ref<HTMLButtonElement>;
  time: number;
  isVisible?: boolean;
  onToggleClock?: (isVisible: boolean) => void;
  position?: Position | undefined;
  onSetPosition: (position: Position) => void;
  size?: Size | undefined;
  onSetSize: (size: Size) => void;
  onToggleOptions?: () => void;
  isOptionsMenuVisible?: boolean;
}> = ({
  optionsElement,
  time,
  isVisible: isClockVisible = true,
  onToggleClock,
  position = { x: 0, y: 0 },
  onSetPosition,
  size = {
    width: MIN_WIDTH,
    height: MIN_HEIGHT,
  },
  onSetSize,
  onToggleOptions,
  isOptionsMenuVisible = false,
}) => {
  const { t } = useWebmapReactTranslation();
  const clockElement = useRef<HTMLDivElement | null>(null);

  useMakeSureContainerStaysInsideWindow(clockElement, position, onSetPosition);

  const [resizeFontSize, setResizeFontSize] = useState<number>(
    FONT_SIZE_WIDTH_RATIO * size.width,
  );
  const [isResizing, setIsResizing] = useState<boolean>(false);

  // While resizing, use the local font size. Once you finish and the width has been updated, revert back to the size as determined from width
  const fontSize = isResizing
    ? resizeFontSize
    : FONT_SIZE_WIDTH_RATIO * size.width;

  const toggleClock = (isClockVisible: boolean): void => {
    onToggleClock?.(isClockVisible);
  };

  const setPosition = (position: Position): void => {
    onSetPosition?.(position);
  };

  const setSize = (size: Size): void => {
    onSetSize?.(size);
  };

  const toggleOptions = (): void => {
    onToggleOptions?.();
  };

  return (
    <>
      {!isClockVisible ? (
        <CustomIconButton
          aria-label={t('clock-show')}
          onClick={() => {
            toggleClock(true);
          }}
          sx={{
            position: 'absolute',
            top: 16,
            right: 8,
            zIndex: 1,
          }}
          tooltipTitle={t('clock-show')}
          variant="boxed"
        >
          <ClockIcon />
        </CustomIconButton>
      ) : (
        <Draggable
          bounds="parent"
          cancel=".MuiButtonBase-root"
          nodeRef={clockElement}
          onStop={(_event, position): void => {
            setPosition({ x: position.x, y: position.y });
          }}
          position={position}
        >
          <div
            ref={clockElement}
            style={{
              position: 'absolute',
              zIndex: 1001,
              top: '10px',
              right: '10px',
            }}
          >
            <Resizable
              enable={resizeHandlePlacement}
              lockAspectRatio
              minHeight={MIN_HEIGHT}
              minWidth={MIN_WIDTH}
              nodeRef={clockElement}
              onResize={(_event, _direction, _ref, delta): void => {
                const width = size.width + delta.width;
                const fontSize = FONT_SIZE_WIDTH_RATIO * width;
                setResizeFontSize(fontSize);
              }}
              onResizeStart={(): void => {
                setIsResizing(true);
                setResizeFontSize(FONT_SIZE_WIDTH_RATIO * size.width);
              }}
              onResizeStop={(_event, _direction, _ref, delta): void => {
                const width = size.width + delta.width;
                const height = size.height + delta.height;
                setIsResizing(false);
                setResizeFontSize(FONT_SIZE_WIDTH_RATIO * width);
                setSize({
                  width,
                  height,
                });
              }}
              size={size}
            >
              <ClockContainer
                fontSize={fontSize}
                isOptionsMenuVisible={isOptionsMenuVisible}
                onToggleClock={(): void => {
                  toggleClock(false);
                }}
                onToggleOptions={toggleOptions}
                optionsElement={optionsElement}
                time={time}
              />
            </Resizable>
          </div>
        </Draggable>
      )}
    </>
  );
};
